import * as React from "react"
import { useMemo, useState } from "react"
import MonacoEditor from "@monaco-editor/react"
import { Placeholder } from "@tiptap/extension-placeholder"
import { useEditor } from "@tiptap/react"
import { StarterKit } from "@tiptap/starter-kit"
import RichTextEditor, { RowTextAlign, TextColors } from "@imminently/decisively-ui/RichTextEditor"

import { ComponentPage, ComponentSection } from "../components/ComponentPage"

const INITIAL_CONTENT =
  '<h2>Release notes</h2><p>This release adds <strong>attribute tokens</strong> to templates, with <span data-color="blue" style="color: #3b82f6">text colours</span>, <span data-highlight="yellow" style="background-color: rgba(234, 179, 8, 0.28)">highlights</span> and <span data-badge="green" style="background-color: #16a34a; color: #ffffff; border-radius: 4px; padding: 0 4px">badges</span>. Read the <a href="https://decisively.io">docs</a>.</p><ul><li>Faster search</li><li>Inline formatting</li></ul><p>Line one</p><p>Line two</p><p>Line three</p>'

const BLOCK_TAG = /(<\/?(?:h[1-6]|p|ul|ol|li|blockquote|pre|hr)\b[^>]*>)/

/**
 * Puts each block of the editor's one-line HTML on its own indented line. A
 * block holding only inline markup stays on one line, e.g. `<p>Hi <b>there</b></p>`.
 */
function formatHtml(html: string): string {
  const tokens = html.split(BLOCK_TAG).filter(Boolean)
  const isBlock = (token: string | undefined) => token !== undefined && BLOCK_TAG.test(token)
  const lines: string[] = []
  let depth = 0
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]
    const indent = "  ".repeat(depth)
    if (!isBlock(token)) {
      lines.push(indent + token)
    } else if (token.startsWith("</")) {
      depth = Math.max(0, depth - 1)
      lines.push("  ".repeat(depth) + token)
    } else if (token.startsWith("<hr")) {
      lines.push(indent + token)
    } else if (tokens[i + 1]?.startsWith("</")) {
      // Empty block, e.g. the trailing <p></p>.
      lines.push(indent + token + tokens[i + 1])
      i += 1
    } else if (!isBlock(tokens[i + 1]) && tokens[i + 2]?.startsWith("</")) {
      lines.push(indent + token + tokens[i + 1] + tokens[i + 2])
      i += 2
    } else {
      lines.push(indent + token)
      depth += 1
    }
  }
  return lines.join("\n")
}

export function RichTextEditorPage() {
  const [html, setHtml] = useState(INITIAL_CONTENT)
  const formattedHtml = useMemo(() => formatHtml(html), [html])

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] }, link: { openOnClick: false } }),
      TextColors,
      RowTextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Start typing…" }),
    ],
    content: INITIAL_CONTENT,
    onUpdate: ({ editor: current }) => setHtml(current.getHTML()),
  })

  return (
    <ComponentPage
      title="Rich Text Editor"
      description="Tiptap editor shell on the deci-ui toolbar and buttons. The caller owns the editor and its extensions and composes the toolbar from builtin controls, or its own via RichTextEditor.Control."
    >
      <ComponentSection
        title="Full toolbar"
        description="Turn into (text, headings, code block, quote), colours (text colour, highlight, badge) and marks, then link, alignment and lists. Groups are separated by a divider automatically. Alignment applies to the paragraphs at the cursor. Hover a link for a card to open, copy, edit or remove it."
      >
        <RichTextEditor editor={editor}>
          <RichTextEditor.Toolbar>
            <RichTextEditor.Group>
              <RichTextEditor.TurnInto />
            </RichTextEditor.Group>
            <RichTextEditor.Group>
              <RichTextEditor.Color />
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
            </RichTextEditor.Group>
            <RichTextEditor.Group>
              <RichTextEditor.Link />
              <RichTextEditor.Align />
              <RichTextEditor.Lists />
            </RichTextEditor.Group>
          </RichTextEditor.Toolbar>
          <RichTextEditor.Content />
        </RichTextEditor>
        <div className="overflow-hidden rounded-md border border-border">
          <MonacoEditor
            height="320px"
            language="html"
            theme="vs-light"
            value={formattedHtml}
            options={{
              readOnly: true,
              domReadOnly: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              fontSize: 13,
              lineNumbers: "on",
              folding: true,
              wordWrap: "on",
              padding: { top: 12, bottom: 12 },
            }}
          />
        </div>
      </ComponentSection>
    </ComponentPage>
  )
}
